<template>
    <section class="tw:flex tw:flex-col tw:gap-4 tw:sm:gap-6">
        <div v-if="loading" class="tw:flex tw:flex-col tw:gap-3 tw:sm:gap-6">
            <Skeleton>
                <template #skeleton>
                    <div class="tw:flex tw:flex-col tw:gap-3 tw:md:flex-row tw:md:items-center tw:md:justify-between">
                        <div class="tw:flex tw:flex-col tw:gap-3">
                            <SkeletonItem class="tw:w-44" height="tw:h-4" />
                            <SkeletonItem class="tw:w-36" height="tw:h-10" />
                            <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:mt-3">
                                <SkeletonItem class="tw:w-56" height="tw:h-4" />
                            </div>
                        </div>
                        <div class="tw:flex tw:justify-end tw:md:shrink-0">
                            <SkeletonItem class="tw:w-48" height="tw:h-10 tw:rounded-md" />
                        </div>
                    </div>
                </template>
            </Skeleton>

            <Skeleton>
                <template #skeleton>
                    <div class="tw:flex tw:items-center tw:mb-4">
                        <SkeletonItem class="tw:w-20" height="tw:h-5 tw:mr-15" />
                        <SkeletonItem class="tw:w-38" height="tw:h-5" />

                        <div class="tw:flex tw:gap-2 tw:ml-auto">
                            <SkeletonItem class="tw:w-25" height="tw:h-5" />
                        </div>
                    </div>
                    <div class="tw:flex tw:flex-col">
                        <div class="tw:hidden tw:grid-cols-[112px_minmax(0,1fr)_96px] tw:gap-4 tw:border-b tw:border-slate-100 tw:px-4 tw:pb-3 tw:md:grid">
                            <SkeletonItem class="tw:w-12" height="tw:h-3" />
                            <SkeletonItem class="tw:w-24" height="tw:h-3" />
                            <SkeletonItem class="tw:w-14 tw:ml-auto" height="tw:h-3" />
                        </div>
                        <div v-for="n in 4" :key="n" class="tw:flex tw:items-center tw:justify-between tw:gap-4 tw:border-b tw:border-slate-100 tw:px-4 tw:py-4 tw:last:border-b-0">
                            <div class="tw:w-24 tw:hidden tw:md:block">
                                <SkeletonItem class="tw:w-20" height="tw:h-4" />
                            </div>
                            <div class="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-3">
                                <SkeletonItem class="tw:w-10 tw:h-10 tw:rounded-xl tw:shrink-0" />
                                <div class="tw:flex tw:flex-col tw:gap-2 tw:min-w-0 tw:flex-1">
                                    <SkeletonItem class="tw:w-40" height="tw:h-4" />
                                    <SkeletonItem class="tw:w-28" height="tw:h-3" />
                                </div>
                            </div>
                            <SkeletonItem class="tw:w-16 tw:shrink-0" height="tw:h-4" />
                        </div>
                        <SkeletonItem class="tw:mt-4 tw:w-48 tw:self-center" height="tw:h-4" />
                    </div>
                </template>
            </Skeleton>
        </div>

        <CardFront
            v-else-if="error"
            custom-classes="tw:py-16 tw:text-center tw:items-center"
        >
            <i class="material-icons tw:text-5xl tw:text-red-300">error_outline</i>
            <p class="tw:mt-4 tw:font-semibold tw:text-slate-700">{{ $t('perfil.monedero_error_cargar') }}</p>
            <p class="tw:text-sm tw:text-slate-400 tw:mt-1">{{ error }}</p>
        </CardFront>

        <template v-else>
            <CardFront custom-classes="tw:p-5 tw:sm:p-6">
                <div class="tw:flex tw:flex-col tw:gap-5 tw:md:flex-row tw:md:flex-nowrap tw:md:items-center tw:md:justify-between tw:md:gap-6">
                    <div class="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-4 tw:sm:gap-5 tw:md:pr-2">
                        <div>
                            <p class="tw:mb-1 tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-slate-400">
                                {{ $t('perfil.monedero_saldo_total_disponible') }}
                            </p>
                            <p class="tw:mb-0 tw:flex tw:flex-wrap tw:items-center tw:gap-1 tw:text-4xl tw:font-black tw:tracking-tight tw:text-slate-900 tw:sm:text-5xl">
                                <span>{{ totalUnitsValueLabel }}</span>
                                <span class="tw:text-red-600">U</span>
                                <i class="material-icons tw:text-[0.7em] tw:text-red-600" aria-hidden="true">token</i>
                            </p>
                        </div>

                        <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:text-sm tw:text-slate-500">
                            <span class="tw:inline-flex tw:gap-1 tw:items-center">
                                <i class="material-icons tw:text-base tw:text-slate-400" aria-hidden="true">info</i>
                                {{ balanceSummaryLabel }}
                            </span>
                        </div>
                    </div>

                    <div class="tw:flex tw:w-full tw:flex-col tw:gap-3 tw:md:w-auto tw:md:shrink-0 tw:md:items-end">
                        <Button
                            type="button"
                            variant="alt"
                            custom-class="tw:w-full tw:md:w-auto tw:md:min-w-[200px]"
                            @click="openWalletInfoModal"
                        >
                            {{ $t('perfil.monedero_como_funciona') }}
                        </Button>
                    </div>
                </div>
            </CardFront>

            <BaseModalFront
                :show="showWalletInfoModal"
                :title="$t('perfil.monedero_info_titulo')"
                :subtitle="$t('perfil.monedero_info_subtitulo')"
                :confirm-text="$t('perfil.entendido')"
                confirm-variant="alt"
                :show-cancel="false"
                size="lg"
                @close="closeWalletInfoModal"
                @confirm="closeWalletInfoModal"
            >
                <div class="tw:grid tw:gap-6 tw:text-base tw:leading-relaxed tw:text-slate-600">
                    <!-- Qué es -->
                    <p class="tw:mb-0 tw:text-slate-700">
                        {{ $t('perfil.monedero_info_que_es') }}
                    </p>

                    <!-- Cómo ganar créditos -->
                    <div>
                        <h3 class="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:font-bold tw:uppercase tw:tracking-widest tw:text-slate-400 tw:mb-3">
                            <i class="material-icons tw:text-lg tw:text-emerald-500">add_circle</i>
                            {{ $t('perfil.monedero_info_como_ganar_titulo') }}
                        </h3>
                        <ul class="tw:grid tw:gap-3 tw:pl-1">
                            <li class="tw:flex tw:gap-3 tw:items-start">
                                <i class="material-icons tw:text-base tw:text-slate-400 tw:mt-0.5 tw:shrink-0">event_busy</i>
                                <span>{{ $t('perfil.monedero_info_ganar_liberar') }}</span>
                            </li>
                            <li class="tw:flex tw:gap-3 tw:items-start">
                                <i class="material-icons tw:text-base tw:text-slate-400 tw:mt-0.5 tw:shrink-0">people</i>
                                <span>{{ $t('perfil.monedero_info_ganar_referidos') }}</span>
                            </li>
                            <li class="tw:flex tw:gap-3 tw:items-start">
                                <i class="material-icons tw:text-base tw:text-slate-400 tw:mt-0.5 tw:shrink-0">campaign</i>
                                <span>{{ $t('perfil.monedero_info_ganar_promociones') }}</span>
                            </li>
                        </ul>
                    </div>

                    <!-- Cómo usar -->
                    <div>
                        <h3 class="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:font-bold tw:uppercase tw:tracking-widest tw:text-slate-400 tw:mb-3">
                            <i class="material-icons tw:text-lg tw:text-amber-500">token</i>
                            {{ $t('perfil.monedero_info_como_usar_titulo') }}
                        </h3>
                        <p class="tw:mb-0">{{ $t('perfil.monedero_info_como_usar_texto') }}</p>
                    </div>

                    <!-- Configuración del club -->
                    <div>
                        <h3 class="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:font-bold tw:uppercase tw:tracking-widest tw:text-slate-400 tw:mb-3">
                            <i class="material-icons tw:text-lg tw:text-blue-500">settings</i>
                            {{ $t('perfil.monedero_info_config_titulo') }}
                        </h3>
                        <p class="tw:mb-0">{{ $t('perfil.monedero_info_config_texto') }}</p>
                    </div>

                    <!-- Historial -->
                    <div>
                        <h3 class="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:font-bold tw:uppercase tw:tracking-widest tw:text-slate-400 tw:mb-3">
                            <i class="material-icons tw:text-lg tw:text-slate-400">receipt_long</i>
                            {{ $t('perfil.monedero_info_historial_titulo') }}
                        </h3>
                        <p class="tw:mb-0">{{ $t('perfil.monedero_info_historial_texto') }}</p>
                    </div>
                </div>
            </BaseModalFront>

            <DataTable
                :title="transactionsTableTitle"
                :columns="balanceColumns"
                :items="transactions"
                grid-template-columns="112px minmax(0, 1fr) 96px"
                empty-icon="account_balance_wallet"
                :empty-title="$t('perfil.sin_monedero_texto')"
                max-body-height="28rem"
            />
        </template>
    </section>
</template>

<script setup>
    import { computed, onMounted, onActivated, ref } from 'vue';
    import { usePerfilBalance } from '@/composables/front/perfil/usePerfilBalance';
    import BaseModalFront from '@/modules/front/ui/BaseModalFront.vue';
    import CardFront from '@/modules/front/ui/CardFront.vue';
    import { Skeleton, SkeletonItem } from '@/modules/front/ui/skeleton';
    import Button from '@/modules/front/ui/Button.vue';
    import DataTable from '@/modules/ui/DataTable.vue';

    const showWalletInfoModal = ref(false);

    const {
        fetchMonedero,
        loading,
        error,
        transactions,
        balanceColumns: balanceColumnsBase,
        transactionsTableTitle,
        totalUnitsValueLabel,
        balanceSummaryLabel,
    } = usePerfilBalance();

    onMounted(() => {
        fetchMonedero();
    });
    onActivated(() => {
        fetchMonedero();
    });

    const amountToneClasses = {
        positive: 'tw:text-emerald-600',
        negative: 'tw:text-rose-600',
        neutral:  'tw:text-slate-500',
    };

    const balanceColumns = computed(() => balanceColumnsBase.value.map(column => {
        if (column.key === 'date') {
            return {
                ...column,
                valueClass: 'tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-slate-500 tw:md:text-[13px]',
            };
        }

        if (column.key === 'description') {
            return {
                ...column,
                valueClass: 'tw:font-semibold tw:uppercase tw:tracking-[0.04em] tw:text-slate-900 tw:md:text-[13px]',
            };
        }

        if (column.key === 'amount') {
            return {
                ...column,
                valueClass: (_value, item) => [
                    'tw:text-left tw:font-black tw:uppercase tw:tracking-[0.02em] tw:md:text-right tw:md:text-[13px]',
                    amountToneClasses[item.amountTone] || amountToneClasses.neutral,
                ],
            };
        }

        return column;
    }));

    const openWalletInfoModal = () => {
        showWalletInfoModal.value = true;
    };

    const closeWalletInfoModal = () => {
        showWalletInfoModal.value = false;
    };
</script>

<style scoped>
    .balance-cta.button.effect-button::before {
        display: none;
    }

    .balance-cta.button.effect-button:hover {
        transform: none;
    }

    .balance-cta-secondary.button.effect-button,
    .balance-cta-secondary.button.effect-button:hover,
    .balance-cta-secondary.button.effect-button:focus-visible {
        color: #334155 !important;
    }
</style>
